/**
 * PQL printer — canonical text for an AST. The canonical form is the fixed
 * point of parse ∘ print: `parse(print(ast))` is structurally identical to
 * `ast` (modulo leaf ids/labels), and printing a parsed statement normalizes
 * its formatting. Negation sugar is reconstructed (NOT(in [v]) → `field != v`,
 * NOT(range ≤ n) → `field > n`, …) so statements read the way people wrote them.
 */

import { isCombinator, type FilterRule, type PqlQuery, type QueryNode, type SortSpec } from './types.ts';

// Values only. Deliberately narrower than the tokenizer's IDENT_RE: `-` and `.`
// are excluded so hyphenated/dotted values (`q3-launch`, a letter-led UUID, an
// `enterprise.eu` segment) print quoted and survive re-parsing. The tokenizer
// tries DURATION_RE (`-30d`) and NUMBER_RE before IDENT_RE, so a bareword `-`
// cannot be read back — quote at the printer rather than widen the tokenizer.
const BAREWORD_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;
const NUMBER_RE = /^-?\d+(?:\.\d+)?$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const KEYWORDS = new Set(['and', 'or', 'not', 'in', 'is', 'empty', 'order', 'by', 'asc', 'desc', 'true', 'false']);

function formatFieldName(key: string): string {
  return key.startsWith('meta:') ? `meta.${key.slice(5)}` : key;
}

/**
 * Print a literal value the tokenizer can read back. Exported because the
 * autocomplete (complete.ts) and the golden-set grader insert canonical value
 * text too, and a second copy of this rule is what produced #650.
 */
export function formatValue(v: string): string {
  if ((BAREWORD_RE.test(v) && !KEYWORDS.has(v.toLowerCase())) || NUMBER_RE.test(v) || DATE_RE.test(v)) {
    return v;
  }
  return `"${v.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

function lo(value: unknown): number {
  return typeof value === 'number' ? value : -Infinity;
}
function hi(value: unknown): number {
  return typeof value === 'number' ? value : Infinity;
}

/** Print a positive (non-negated) leaf. */
function printLeaf(rule: FilterRule): string {
  const field = formatFieldName(rule.field);
  switch (rule.operator) {
    case 'in': {
      const values = Array.isArray(rule.value) ? (rule.value as string[]) : [String(rule.value)];
      return values.length === 1
        ? `${field} = ${formatValue(values[0])}`
        : `${field} IN (${values.map(formatValue).join(', ')})`;
    }
    case 'eq':
      return `${field} = ${formatValue(String(rule.value))}`;
    case 'boolean':
      return `${field} = ${rule.value ? 'true' : 'false'}`;
    case 'text':
      return `${field} ~ ${formatValue(String(rule.value))}`;
    case 'range': {
      const [a, b] = rule.value as [number, number];
      const min = lo(a);
      const max = hi(b);
      if (min === max) return `${field} = ${min}`;
      if (max === Infinity) return `${field} >= ${min}`;
      if (min === -Infinity) return `${field} <= ${max}`;
      return `${field} = ${min}..${max}`;
    }
    case 'date-range': {
      const [start, end] = rule.value as [string, string];
      if (start && end) return start === end ? `${field} = ${start}` : `${field} = ${start}..${end}`;
      if (start) return `${field} >= ${start}`;
      if (end) return `${field} <= ${end}`;
      return `${field} IS EMPTY`; // degenerate stored value; closest meaning
    }
    case 'empty':
      return `${field} IS EMPTY`;
    case 'gt':
      return `${field} > ${rule.value}`;
    case 'lt':
      return `${field} < ${rule.value}`;
    case 'date-after':
      return `${field} > ${rule.value}`;
    case 'date-before':
      return `${field} < ${rule.value}`;
    default:
      return `${field} = ${formatValue(String(rule.value))}`;
  }
}

/** Print NOT(leaf) using the negated operator sugar where one exists. */
function printNegatedLeaf(rule: FilterRule): string | null {
  const field = formatFieldName(rule.field);
  switch (rule.operator) {
    case 'in': {
      const values = Array.isArray(rule.value) ? (rule.value as string[]) : [String(rule.value)];
      return values.length === 1
        ? `${field} != ${formatValue(values[0])}`
        : `${field} NOT IN (${values.map(formatValue).join(', ')})`;
    }
    case 'eq':
      return `${field} != ${formatValue(String(rule.value))}`;
    case 'text':
      return `${field} !~ ${formatValue(String(rule.value))}`;
    case 'range': {
      // Only the point-range (parsed from `!=`) has negation sugar. Open
      // ranges under NOT stay as NOT (field <= n) — that form includes
      // empty-valued items, unlike `field > n` (see evaluate.ts semantics).
      const [a, b] = rule.value as [number, number];
      const min = lo(a);
      const max = hi(b);
      if (min === max) return `${field} != ${min}`;
      return null;
    }
    case 'date-range': {
      const [start, end] = rule.value as [string, string];
      if (start && end && start === end) return `${field} != ${start}`;
      return null;
    }
    case 'empty':
      return `${field} IS NOT EMPTY`;
    default:
      return null;
  }
}

function printNode(node: QueryNode, parent: 'and' | 'or' | 'not' | null): string {
  if (!isCombinator(node)) return printLeaf(node);

  if (node.op === 'not') {
    if (!isCombinator(node.child)) {
      const sugar = printNegatedLeaf(node.child);
      if (sugar) return sugar;
      return `NOT (${printNode(node.child, 'not')})`;
    }
    return `NOT (${printNode(node.child, 'not')})`;
  }

  const joined = node.children
    .map((c: QueryNode) => printNode(c, node.op))
    .join(node.op === 'and' ? ' AND ' : ' OR ');

  // AND binds tighter than OR: an OR nested under an AND needs parens; an AND
  // under an OR does not. Anything under NOT was parenthesized above.
  if (node.op === 'or' && parent === 'and') return `(${joined})`;
  return joined;
}

export function printQuery(query: PqlQuery): string {
  const parts: string[] = [];
  if (query.where) parts.push(printNode(query.where, null));
  if (query.orderBy.length > 0) {
    parts.push(
      `ORDER BY ${query.orderBy
        .map((s: SortSpec) => `${formatFieldName(s.field)} ${s.direction.toUpperCase()}`)
        .join(', ')}`,
    );
  }
  return parts.join(' ');
}
